import { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { EquipmentUtilities } from "../../../EquipmentUtilities";
import Toast from "../../../helpers/Toast";
import { EquipmentDTO } from "../../../models/Equipment/EquipmentDTO";
import { EquipmentEditProps } from "../../../models/Equipment/EquipmentProps";
import { UpdateEquipmentDTO } from "../../../models/Equipment/UpdateEquipmentDTO";
import { EquipmentService } from "../../../services/EquipmentService";
import ButtonComponent from "../../ButtonComponent";
import ClientEquipmentBidirecional from "../../ClientEquipmentBidirecional/ClientEquipmentBidirecional";
import ModalComponent from "../../ModalComponent";
import { SelectItemsProps } from "../../Select2Component";
import useValidationSchema, { FieldConfig } from "../../Validations";

export default function EquipamentEditB({ setPageTitle }: EquipmentEditProps) {
    const { id } = useParams<{ id: string }>();
    const intl = useIntl();
    const navigate = useNavigate();
    const equipmentService = new EquipmentService();

    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const [isOpen, setIsOpen] = useState<boolean>(false);
    
    // props para novo componente
    const [isActiveProjectModule, setIsActiveProjectModule] = useState<boolean>(false);
    const [projectSelected, setProjectSelected] = useState<SelectItemsProps | null>(null);
    const [clientSelected, setClientSelected] = useState<SelectItemsProps | null>(null);
    const [serialNumberSelected, setSerialNumberSelected] = useState<SelectItemsProps | null>(null);
    const [equipment, setEquipment] = useState<EquipmentDTO>({} as EquipmentDTO);
    const [isSearchEquipmentLoading, setIsSearchEquipmentLoading] = useState<boolean>(false);
    const [carPlateSelected, setCarPlateSelected] = useState<SelectItemsProps | null>(null);

    useEffect(() => {
        const fetchAndSetEquipment = async () => {
            const equipment = await EquipmentUtilities.GetEquipment(String(id));

            if (!equipment) {
                Toast.Show("error", "Dados insuficiente para obter os dados do equipamento.");
                return;
            }

            setEquipment(equipment);

            setClientSelected({
                value: equipment.client?.erpId || '',
                label: equipment.client?.name || '',
            });

            setSerialNumberSelected({
                value: equipment.serialNumber || '',
                label: equipment.serialNumber || '',
            });

            setCarPlateSelected({
                value: equipment.carPlate || '',
                label: equipment.carPlate || '',
            });

        };

        if (id) {
            fetchAndSetEquipment();
        }
    }, [id]);

    useEffect(() => {
        if (equipment?.carPlate !== undefined && equipment?.serialNumber !== undefined) {
            setPageTitle(`Equipamento ${equipment?.carPlate} - ${equipment?.serialNumber}`);
        }
    }, [setPageTitle, equipment]);

    const fields: FieldConfig[] = [
        ["carPlate", "string", "Screens.Equipments.Edit.CarPlateRequired"],
        ["serialNumber", "string", "Screens.Equipments.Edit.SerialNumberRequired"],
        ["brand", "string", "Screens.Equipments.Edit.BrandRequired"],
        ["model", "string", "Screens.Equipments.Edit.ModelRequired"]
    ];

    const schemaClientValidation = useValidationSchema(fields);

    const editEquipment = async () => {

        const updatedEquipment: UpdateEquipmentDTO = {
            erpId: equipment?.erpId ?? null,
            name: equipment?.name ?? null,
            carPlate: equipment?.carPlate ?? null,
            carPlateValidation: true,
            serialNumber: equipment?.serialNumber ?? null,
            brand: equipment?.brand ?? null,
            model: equipment?.model ?? null,
            clientErpId: clientSelected?.value! ?? null,
        };

        const { error } = schemaClientValidation.validate(updatedEquipment);
        if (error) {
            Toast.Show("error", error.details.map(detail => detail.message).join(', '));
            setLoadingUpdate(false);
            return;
        }
        setLoadingUpdate(true);
        const response = await equipmentService.Edit(updatedEquipment);
        setLoadingUpdate(false);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            return;
        }
        navigate(-1);
        Toast.Show("success", response.message);
    };

    const deleteEquipment = async () => {
        setLoadingUpdate(true);
        const response = await equipmentService.Delete(id);
        setLoadingUpdate(false);

        if (response.success !== true) {
            Toast.Show("error", response.message);
            return;
        }
        navigate(-1);
        Toast.Show("success", response.message);
    };

    return (
        <>
            <Row
                style={{
                    margin: "0px",
                    padding: "0px",
                    alignItems: "flex-end",
                    alignContent: "flex-end",
                }}
            >
                <ClientEquipmentBidirecional
                    isActiveProjectModule={isActiveProjectModule}
                    projectSelected={projectSelected}
                    clientSelected={clientSelected}
                    setClientSelected={setClientSelected}
                    serialNumberSelected={serialNumberSelected}
                    setSerialNumberSelected={setSerialNumberSelected}
                    equipment={equipment}
                    setEquipment={setEquipment}
                    isSearchEquipmentLoading={isSearchEquipmentLoading}
                    setIsSearchEquipmentLoading={setIsSearchEquipmentLoading}
                    isActiveSearchByCarPlate={true}
                    carPlateSelected={carPlateSelected}
                    setCarPlateSelected={setCarPlateSelected}
                />
            </Row>


            <Row style={{ marginTop: "10px", paddingLeft: "13px", paddingBottom: "13px", paddingRight: "13px" }}>
                <Col xl={2} lg={6} md={6} sm={6} xs={6}>
                    <ButtonComponent
                        disabled={loadingUpdate}
                        loading={loadingUpdate}
                        text={intl.formatMessage({ id: "EquipmentEditB.Save" })}
                        onClick={editEquipment}
                        style={{ width: "100%" }}
                    />
                </Col>
                <Col xl={2} lg={6} md={6} sm={6} xs={6}>
                    <ButtonComponent
                        disabled={loadingUpdate}
                        loading={loadingUpdate}
                        text={intl.formatMessage({ id: "EquipmentEditB.Remove" })}
                        onClick={() => {
                            setIsOpen(true);
                        }}
                        style={{ width: "100%" }}
                    />
                </Col>
            </Row>

            <ModalComponent
                IsOpen={isOpen}
                text={`Tem a certeza que quer remover o equipamento ${equipment.carPlate!}`}
                OnClickRight={() => setIsOpen(false)}
                OnClickLeft={() => {
                    setIsOpen(false);
                    deleteEquipment();
                }}
                title={"Remover Equipamento"}
                buttonRight={"Não"}
                buttonLeft={"Sim"}
            />
        </>
    );
}
